import moment from 'moment-timezone';
import { convertToDegrees } from './helpers';

/**
 * Initial clock state
 */
const initTime = Date.now();
const ONE_DAY = 1000 * 60 * 60 * 24; // 1 day
const defaultState = {
  speed: 1,
  startTime: initTime,
  stopTime: initTime + ONE_DAY, // +1 day
  time: initTime,
  playing: false,
  _userPlay: false,
  _pauseCount: 0,
};

export default class Clock {
  realDt = 0.0;
  realTime = 0.0;
  lastTime = undefined;
  lastPlaying = undefined;
  lastSpeed = undefined;

  update(appTime, externalEventEmitter, store) {
    this.realDt = appTime - this.realTime;
    this.realTime = appTime;

    store.dispatch({ type: 'TICK', dt: this.realDt });

    // Notify external parties that the time has changed
    const newTime = store.getState().clock.time;
    if (newTime !== this.lastTime) {
      externalEventEmitter.emit('clock.change', newTime);
    }

    // Notify external parties that the play/pause state has changed
    const newPlaying = store.getState().clock.playing;
    if (newPlaying !== this.lastPlaying) {
      externalEventEmitter.emit('clock.playing', newPlaying);
    }

    // Notify external parties that the speed has changed
    const newSpeed = store.getState().clock.speed;
    if (newSpeed !== this.lastSpeed) {
      externalEventEmitter.emit('clock.speed', newSpeed);
    }

    this.lastPlaying = newPlaying;
    this.lastTime = newTime;
    this.lastSpeed = newSpeed;
  }

  onSceneLoad(scene, dispatch) {
    setGMTOffset(scene.startPoint, dispatch);

    dispatch({ type: 'SET_TIME', time: scene.startPoint.time });
    dispatch({ type: 'SET_START_TIME', startTime: scene.startPoint.time });
    dispatch({ type: 'SET_SPEED', speed: scene.sceneSettings.speed });

    // If an end time was not specified by scene, use the default of startTime + one day
    const hasEndTime = scene.endPoint && scene.endPoint.time;
    const stopTime = hasEndTime ? scene.endPoint.time : scene.startPoint.time + ONE_DAY;
    dispatch({ type: 'SET_STOP_TIME', stopTime });
  }
}

// Gets GMT offset based on location and time (time needed for DLS)
export const gmtOffsetAtTimezone = (time, locationData) => {
  let gmtOffset;
  if (locationData.timezoneId) {
    // -1 as moment returns the flipped version
    gmtOffset = moment.tz.zone(locationData.timezoneId).utcOffset(time) * -1;
  } else if (locationData.rawOffset) {
    // 60 makes the conversion into minutes as rawOffset is in hours
    gmtOffset = locationData.rawOffset * 60;
  }
  return gmtOffset;
};

// Sets GMT offset in the store if suitable information returns from geonames server
const setGMTOffset = (startPoint, dispatch) => {
  // XHR request that gets the startPoint and then sets the timezone Offset
  const locationDataURL = `https://ws.geonames.net/timezoneJSON?
    &lat=${convertToDegrees(startPoint.latitude)}
    &lng=${convertToDegrees(startPoint.longitude)}
    &username=doarama_mod`;

  const gmtXHR = new XMLHttpRequest();
  gmtXHR.open('GET', locationDataURL, true);
  gmtXHR.onload = () => {
    if (gmtXHR.status === 200) {
      const response = JSON.parse(gmtXHR.response);
      const gmtOffset = gmtOffsetAtTimezone(startPoint.time, response);

      dispatch({
        type: 'SET_GMT_OFFSET',
        gmtOffset,
      });
    }
  };
  gmtXHR.send();
};

/**
 * Handles state changes to the clock.
 * TICK should be called for each RAF.
 */
export const clockReducer = (state = defaultState, action) => {
  switch (action.type) {
    case 'TOGGLE_PLAYING':
      return {
        ...state,
        _userPlay: !state._userPlay,
        playing: !state._userPlay && state._pauseCount <= 0,
      };
    case 'STOP_PLAYING':
      return {
        ...state,
        _userPlay: false,
        playing: false,
      };
    case 'START_PLAYING':
      return {
        ...state,
        _userPlay: true,
        playing: state._pauseCount <= 0,
      };

    // Certain systems would like to temporarily override the user's play/pause selection. This can happen concurrently,
    // so multiple systems need to be able to "push" a pause and pop it when done, without overriding the user's choice.
    case 'PUSH_PAUSE':
      return {
        ...state,
        _pauseCount: state._pauseCount + 1,
        playing: state._userPlay && state._pauseCount + 1 <= 0,
      };
    case 'POP_PAUSE':
      return {
        ...state,
        _pauseCount: state._pauseCount - 1,
        playing: state._userPlay && state._pauseCount - 1 <= 0,
      };

    case 'SET_SPEED':
      return {
        ...state,
        speed: action.speed,
      };
    case 'SET_TIME':
      return {
        ...state,
        time: action.time,
      };
    case 'SET_START_TIME':
      return {
        ...state,
        startTime: action.startTime,
      };
    case 'SET_STOP_TIME':
      return {
        ...state,
        stopTime: action.stopTime,
      };
    case 'SET_GMT_OFFSET':
      return {
        ...state,
        gmtOffset: action.gmtOffset,
      };
    case 'TIME_RESET':
      return {
        ...state,
        time: state.startTime,
      };
    case 'TICK': {
      const prevTime = state.time;
      let newTime = state.time;
      if (state.playing) {
        newTime += action.dt * state.speed;
      }
      return {
        ...state,
        prevTime: prevTime,
        time: newTime,
      };
    }
    default:
      return state;
  }
};
